/**
 * @preserve
 * gcoord 1.0.7, geographic coordinate library
 * Copyright (c) 2025 Jiulong Hu <me@hujiulong.com>
 */

const { sin, cos, sqrt, abs, PI, atan2, log, tan, exp } = Math

// 常量定义
const EARTH_RADIUS = 6378245
const ECCENTRICITY_SQ = 0.006693421622965823
const METER_PER_DEGREE = 20037508.342789244

// 检查坐标是否在中国境内
function isInChina (lng, lat) {
  return lng >= 72.004 && lng <= 137.8347 && lat >= 0.8293 && lat <= 55.8271
}

// GCJ-02 加密算法辅助函数
function deltaLng (lng, lat) {
  let result = -100 + 2 * lng + 3 * lat + 0.2 * lat * lat + 0.1 * lng * lat + 0.2 * sqrt(abs(lng))
  result += (20 * sin(6 * lng * PI) + 20 * sin(2 * lng * PI)) * 2 / 3
  result += (20 * sin(lat * PI) + 40 * sin(lat / 3 * PI)) * 2 / 3
  result += (160 * sin(lat / 12 * PI) + 320 * sin(lat * PI / 30)) * 2 / 3
  return result
}

function deltaLat (lng, lat) {
  let result = 300 + lng + 2 * lat + 0.1 * lng * lng + 0.1 * lng * lat + 0.1 * sqrt(abs(lng))
  result += (20 * sin(6 * lng * PI) + 20 * sin(2 * lng * PI)) * 2 / 3
  result += (20 * sin(lng * PI) + 40 * sin(lng / 3 * PI)) * 2 / 3
  result += (150 * sin(lng / 12 * PI) + 300 * sin(lng / 30 * PI)) * 2 / 3
  return result
}

// WGS84 转 GCJ02
function wgs84ToGcj02 (coord) {
  const [lng, lat] = coord
  if (!isInChina(lng, lat)) return [lng, lat]

  const dLat = deltaLat(lng - 105, lat - 35)
  const dLng = deltaLng(lng - 105, lat - 35)

  const radLat = lat / 180 * PI
  const magic = sin(radLat)
  const magicSq = 1 - ECCENTRICITY_SQ * magic * magic
  const sqrtMagic = sqrt(magicSq)

  const deltaLngResult = dLng * 180 / (EARTH_RADIUS / sqrtMagic * cos(radLat) * PI)
  const deltaLatResult = dLat * 180 / (EARTH_RADIUS * (1 - ECCENTRICITY_SQ) / (magicSq * sqrtMagic) * PI)

  return [lng + deltaLngResult, lat + deltaLatResult]
}

// GCJ02 转 WGS84
export function gcj02ToWgs84 (coord) {
  const [lng, lat] = coord
  if (!isInChina(lng, lat)) return [lng, lat]

  let [tempLng, tempLat] = [lng, lat]
  let [deltaLng, deltaLat] = wgs84ToGcj02([tempLng, tempLat])

  while (abs(deltaLng - lng) > 1e-6 || abs(deltaLat - lat) > 1e-6) {
    tempLng -= deltaLng - lng
    tempLat -= deltaLat - lat;
    [deltaLng, deltaLat] = wgs84ToGcj02([tempLng, tempLat])
  }

  return [tempLng, tempLat]
}

// 百度坐标系转换相关常量
const BAIDU_OFFSET_LNG = 0.0065
const BAIDU_OFFSET_LAT = 0.006
const BAIDU_FACTOR = PI * 3000.0 / 180.0

// GCJ02 转 BD09
function gcj02ToBd09 (coord) {
  const [lng, lat] = coord
  const z = sqrt(lng * lng + lat * lat) + 0.00002 * sin(lat * BAIDU_FACTOR)
  const theta = atan2(lat, lng) + 0.000003 * cos(lng * BAIDU_FACTOR)
  const bdLng = z * cos(theta) + BAIDU_OFFSET_LNG
  const bdLat = z * sin(theta) + BAIDU_OFFSET_LAT
  return [bdLng, bdLat]
}

// BD09 转 GCJ02
function bd09ToGcj02 (coord) {
  const [lng, lat] = coord
  const x = lng - BAIDU_OFFSET_LNG
  const y = lat - BAIDU_OFFSET_LAT
  const z = sqrt(x * x + y * y) - 0.00002 * sin(y * BAIDU_FACTOR)
  const theta = atan2(y, x) - 0.000003 * cos(x * BAIDU_FACTOR)
  const gcjLng = z * cos(theta)
  const gcjLat = z * sin(theta)
  return [gcjLng, gcjLat]
}

// WGS84 转 Web墨卡托
function wgs84ToWebMercator (coord) {
  const [lng, lat] = coord
  const x = EARTH_RADIUS * lng * PI / 180
  const y = EARTH_RADIUS * log(tan(PI * 0.25 + 0.5 * lat * PI / 180))

  const boundedX = x > METER_PER_DEGREE ? METER_PER_DEGREE : (x < -METER_PER_DEGREE ? -METER_PER_DEGREE : x)
  const boundedY = y > METER_PER_DEGREE ? METER_PER_DEGREE : (y < -METER_PER_DEGREE ? -METER_PER_DEGREE : y)

  return [boundedX, boundedY]
}

// Web墨卡托 转 WGS84
function webMercatorToWgs84 (coord) {
  const [x, y] = coord
  const lng = x * 180 / (EARTH_RADIUS * PI)
  const lat = (PI * 0.5 - 2 * Math.atan(exp(-y / EARTH_RADIUS))) * 180 / PI
  return [lng, lat]
}

// 百度墨卡托投影参数 - 修正精度问题
const BD09MC_LAT_BOUNDS = [75, 60, 45, 30, 15, 0]

// 使用科学计数法避免精度问题
const BD09MC_PARAMS = [
  [1.410526172116255e-8, 8.98305509648872e-6, -1.9939833816331, 200.9824383106796, -187.2403703815547, 91.6087516669843, -23.38765649603339, 2.57121317296198, -0.03801003308653, 17337981.2],
  [-7.435856389565537e-9, 8.983055097726239e-6, -0.78625201886289, 96.32687599759846, -1.85204757529826, -59.36935905485877, 47.40033549296737, -16.50741931063887, 2.28786674699375, 10260144.86],
  [-3.030883460898826e-8, 8.98305509983578e-6, 0.30071316287616, 59.74293618442277, 7.357984074871, -25.38371002664745, 13.45380521110908, -3.29883767235584, 0.32710905363475, 6856817.37],
  [-1.981981304930552e-8, 8.983055099779535e-6, 0.03278182852591, 40.31678527705744, 0.65659298677277, -4.44255534477492, 0.85341911805263, 0.12923347998204, -0.04625736007561, 4482777.06],
  [3.09191371068437e-9, 8.983055096812155e-6, 0.00006995724062, 23.10934304144901, -0.00023663490511, -0.6321817810242, -0.00663494467273, 0.03430082397953, -0.00466043876332, 2555164.4],
  [2.890871144776878e-9, Number('8.983055095807407e-6'), -3.068298e-8, 7.47137025468032, -3.53937994e-8, -0.02145144861037, -1.234426596e-8, 0.00010322952773, -3.23890364e-8, 826088.5]
]

const BD09MC_TO_BD09_PARAMS = [
  [-0.0015702102444, 111320.7020616939, 1704480524535203, -10338987376042340, 26112667856603880, -35149669176653700, 26595700718403920, -10725012454188240, 1800819912950474, 82.5],
  [0.0008277824516172526, 111320.7020463578, 647795574.6671607, -4082003173.641316, 10774905663.51142, -15171875531.51559, 12053065338.62167, -5124939663.577472, 913311935.9512032, 67.5],
  [0.00337398766765, 111320.7020202162, 4481351.045890365, -23393751.19931662, 79682215.47186455, -115964993.2797253, 97236711.15602145, -43661946.33752821, 8477230.501135234, 52.5],
  [0.00220636496208, 111320.7020209128, 51751.86112841131, 3796837.749470245, 992013.7397791013, -1221952.21711287, 1340652.697009075, -620943.6990984312, 144416.9293806241, 37.5],
  [-0.0003441963504368392, 111320.7020576856, 278.2353980772752, 2485758.690035394, 6070.750963243378, 54821.18345352118, 9540.606633304236, -2710.55326746645, 1405.483844121726, 22.5],
  [-0.0003218135878613132, 111320.7020701615, 0.00369383431289, 823725.6402795718, 0.46104986909093, 2351.343141331292, 1.58060784298199, 8.77738589078284, 0.37238884252424, 7.45]
]

// 百度墨卡托坐标转换
function transformWithParams (x, y, params) {
  const T = abs(y) / params[9]
  let tx = params[0] + params[1] * abs(x)
  let ty = params[2] + params[3] * T + params[4] * Math.pow(T, 2) +
           params[5] * Math.pow(T, 3) + params[6] * Math.pow(T, 4) +
           params[7] * Math.pow(T, 5) + params[8] * Math.pow(T, 6)

  tx *= x < 0 ? -1 : 1
  ty *= y < 0 ? -1 : 1

  return [tx, ty]
}

// BD09 转 百度墨卡托
function bd09ToBd09MC (coord) {
  const [lng, lat] = coord
  let params
  for (let i = 0; i < BD09MC_LAT_BOUNDS.length; i++) {
    if (abs(lat) >= BD09MC_LAT_BOUNDS[i]) {
      params = BD09MC_PARAMS[i]
      break
    }
  }
  return transformWithParams(lng, lat, params)
}

// 百度墨卡托 转 BD09
function bd09MCToBd09 (coord) {
  const [x, y] = coord
  let params
  for (let i = 0; i < BD09MC_LAT_BOUNDS.length; i++) {
    if (abs(y) >= BD09MC_LAT_BOUNDS[i]) {
      params = BD09MC_TO_BD09_PARAMS[i]
      break
    }
  }
  return transformWithParams(x, y, params)
}

// 函数组合工具
function compose (...functions) {
  const length = functions.length - 1
  return function (...args) {
    let result = functions[length].apply(null, args)
    for (let i = length - 1; i >= 0; i--) {
      result = functions[i].call(null, result)
    }
    return result
  }
}

// 坐标系类型枚举
let CRS;
(function (CRS) {
  CRS.WGS84 = 'WGS84'
  CRS.WGS1984 = 'WGS84'
  CRS.EPSG4326 = 'WGS84'
  CRS.GCJ02 = 'GCJ02'
  CRS.AMap = 'GCJ02'
  CRS.BD09 = 'BD09'
  CRS.BD09LL = 'BD09'
  CRS.Baidu = 'BD09'
  CRS.BMap = 'BD09'
  CRS.BD09MC = 'BD09MC'
  CRS.BD09Meter = 'BD09MC'
  CRS.EPSG3857 = 'EPSG3857'
  CRS.EPSG900913 = 'EPSG3857'
  CRS.EPSG102100 = 'EPSG3857'
  CRS.WebMercator = 'EPSG3857'
  CRS.WM = 'EPSG3857'
})(CRS || (CRS = {}))

// 转换映射表
const WGS84_TRANSFORMS = {
  to: {
    [CRS.GCJ02]: wgs84ToGcj02,
    [CRS.BD09]: compose(gcj02ToBd09, wgs84ToGcj02),
    [CRS.BD09MC]: compose(bd09ToBd09MC, gcj02ToBd09, wgs84ToGcj02),
    [CRS.EPSG3857]: wgs84ToWebMercator
  }
}

const GCJ02_TRANSFORMS = {
  to: {
    [CRS.WGS84]: gcj02ToWgs84,
    [CRS.BD09]: gcj02ToBd09,
    [CRS.BD09MC]: compose(bd09ToBd09MC, gcj02ToBd09),
    [CRS.EPSG3857]: compose(wgs84ToWebMercator, gcj02ToWgs84)
  }
}

const BD09_TRANSFORMS = {
  to: {
    [CRS.WGS84]: compose(gcj02ToWgs84, bd09ToGcj02),
    [CRS.GCJ02]: bd09ToGcj02,
    [CRS.EPSG3857]: compose(wgs84ToWebMercator, gcj02ToWgs84, bd09ToGcj02),
    [CRS.BD09MC]: bd09ToBd09MC
  }
}

const EPSG3857_TRANSFORMS = {
  to: {
    [CRS.WGS84]: webMercatorToWgs84,
    [CRS.GCJ02]: compose(wgs84ToGcj02, webMercatorToWgs84),
    [CRS.BD09]: compose(gcj02ToBd09, wgs84ToGcj02, webMercatorToWgs84),
    [CRS.BD09MC]: compose(bd09ToBd09MC, gcj02ToBd09, wgs84ToGcj02, webMercatorToWgs84)
  }
}

const BD09MC_TRANSFORMS = {
  to: {
    [CRS.WGS84]: compose(gcj02ToWgs84, bd09ToGcj02, bd09MCToBd09),
    [CRS.GCJ02]: compose(bd09ToGcj02, bd09MCToBd09),
    [CRS.EPSG3857]: compose(wgs84ToWebMercator, gcj02ToWgs84, bd09ToGcj02, bd09MCToBd09),
    [CRS.BD09]: bd09MCToBd09
  }
}

const TRANSFORM_MAP = {
  WGS84: WGS84_TRANSFORMS,
  GCJ02: GCJ02_TRANSFORMS,
  BD09: BD09_TRANSFORMS,
  EPSG3857: EPSG3857_TRANSFORMS,
  BD09MC: BD09MC_TRANSFORMS
}

// 遍历 GeoJSON 坐标
function traverseGeoJSON (geojson, callback, excludeClosingCoord = false) {
  if (!geojson) return

  let coordIndex = 0
  let featureIndex = 0

  const { type } = geojson
  const isFeatureCollection = type === 'FeatureCollection'
  const isFeature = type === 'Feature'
  const featureCount = isFeatureCollection ? geojson.features.length : 1

  for (let i = 0; i < featureCount; i++) {
    const feature = isFeatureCollection ? geojson.features[i] : geojson
    const geometry = isFeature ? feature.geometry : geojson

    if (!geometry) continue

    const isGeometryCollection = geometry.type === 'GeometryCollection'
    const geometryCount = isGeometryCollection ? geometry.geometries.length : 1

    for (let j = 0; j < geometryCount; j++) {
      const geom = isGeometryCollection ? geometry.geometries[j] : geometry

      if (!geom) continue

      const geomType = geom.type
      const coordinates = geom.coordinates

      const shouldExcludeClosing = excludeClosingCoord &&
                                 (geomType === 'Polygon' || geomType === 'MultiPolygon')

      switch (geomType) {
        case 'Point':
          if (callback(coordinates, coordIndex, i, 0, 0) === false) return false
          coordIndex++
          break

        case 'LineString':
        case 'MultiPoint':
          for (let k = 0; k < coordinates.length; k++) {
            if (callback(coordinates[k], coordIndex, i, k, 0) === false) return false
            coordIndex++
          }
          break

        case 'Polygon':
        case 'MultiLineString':
          for (let k = 0; k < coordinates.length; k++) {
            const ring = coordinates[k]
            const pointCount = shouldExcludeClosing && geomType === 'Polygon' ? ring.length - 1 : ring.length

            for (let l = 0; l < pointCount; l++) {
              if (callback(ring[l], coordIndex, i, k, l) === false) return false
              coordIndex++
            }
          }
          break

        case 'MultiPolygon':
          for (let k = 0; k < coordinates.length; k++) {
            const polygon = coordinates[k]
            for (let l = 0; l < polygon.length; l++) {
              const ring = polygon[l]
              const pointCount = shouldExcludeClosing ? ring.length - 1 : ring.length

              for (let m = 0; m < pointCount; m++) {
                if (callback(ring[m], coordIndex, i, k, l) === false) return false
                coordIndex++
              }
            }
          }
          break

        case 'GeometryCollection':
          for (let k = 0; k < geom.geometries.length; k++) {
            if (traverseGeoJSON(geom.geometries[k], callback, excludeClosingCoord) === false) return false
          }
          break

        default:
          throw new Error('Unknown Geometry Type')
      }
    }
    featureIndex++
  }
}

// 参数验证
function validate (condition, message) {
  if (!condition) {
    throw new Error(message)
  }
}

// 检查是否为数组
function isArray (obj) {
  return Object.prototype.toString.call(obj) === '[object Array]'
}

// 检查是否为有效数字
function isValidNumber (num) {
  return !isNaN(Number(num)) && num !== null && !isArray(num)
}

// 主转换函数
function transform (input, fromCRS, toCRS) {
  validate(!!input, 'The args[0] input coordinate is required')
  validate(!!fromCRS, 'The args[1] original coordinate system is required')
  validate(!!toCRS, 'The args[2] target coordinate system is required')

  if (fromCRS === toCRS) return input

  const fromTransform = TRANSFORM_MAP[fromCRS]
  validate(!!fromTransform, `Invalid original coordinate system: ${fromCRS}`)

  const transformFunc = fromTransform.to[toCRS]
  validate(!!transformFunc, `Invalid target coordinate system: ${toCRS}`)

  const inputType = typeof input
  validate(inputType === 'string' || inputType === 'object', `Invalid input coordinate type: ${inputType}`)

  let coord = input
  if (inputType === 'string') {
    try {
      coord = JSON.parse(input)
    } catch {
      throw new Error(`Invalid input coordinate: ${input}`)
    }
  }

  let isPoint = false
  if (isArray(coord)) {
    validate(coord.length >= 2, `Invalid input coordinate: ${coord}`)
    validate(isValidNumber(coord[0]) && isValidNumber(coord[1]), `Invalid input coordinate: ${coord}`)
    coord = coord.map(Number)
    isPoint = true
  }

  const transformFunction = transformFunc

  if (isPoint) {
    return transformFunction(coord)
  } else {
    traverseGeoJSON(coord, (point) => {
      const [lng, lat] = transformFunction(point)
      point[0] = lng
      point[1] = lat
    })
    return coord
  }
}

// 导出对象
const gcoord = Object.assign(Object.assign({}, CRS), {
  CRSTypes: CRS,
  transform: transform
})

export { gcoord as e }
